import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  FormFeedback,
  Badge,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageService from "../../../services/KangarooServices/PageService";
import toast from "react-hot-toast";
import { ArrowLeft, Save } from "lucide-react";
import { imageBaseApi } from "../../../utilis/imageHelper";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

const PageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);

  // Validation Schema
  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Title is required")
      .min(3, "Title must be at least 3 characters")
      .max(255, "Title must not exceed 255 characters"),
    subtitle: Yup.string()
      .max(500, "Subtitle must not exceed 500 characters"),
    content: Yup.string()
      .required("Content is required")
      .min(10, "Content must be at least 10 characters"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      subtitle: "",
      content: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        // Get the full page data first
        const currentData = await PageService.getDetail(id);
        
        // Update only title, subtitle, and content
        const payload = {
          ...currentData,
          title: values.title,
          subtitle: values.subtitle,
          content: values.content,
        };

        await PageService.update(id, payload);
        toast.success("Page updated successfully");
        navigate(`/pages-detail/${id}`);
      } catch (error) {
        console.log(error);
        toast.error("Failed to update page!");
      }
    },
  });

  // Fetch page detail
  useEffect(() => {
    const fetchPageDetail = async () => {
      try {
        setLoading(true);
        const response = await PageService.getDetail(id);
        setPageData(response);
        formik.setValues({
          title: response.title || "",
          subtitle: response.subtitle || "",
          content: response.content || "",
        });
      } catch (error) {
        console.log(error);
        toast.error("Failed to load page");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPageDetail();
    }
  }, [id]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  if (loading) {
    return (
      <Container fluid className="page-content">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  // Get featured media
  const featuredMedia = pageData?.mediaItems?.find(
    (m) => m.id === pageData?.featuredMediaId
  );

  const breadcrumbItems = [
    { title: "Back to List", link: "/pages-list" },
    { title: "Back to Detail", link: `/pages-detail/${id}` }
  ];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Edit Page Content"
          pageTitle="Page Editor"
          breadcrumbItems={breadcrumbItems}
        />

        <div>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button 
              type="button" 
              className="btn btn-success"
              onClick={handleFormSubmit}
              disabled={formik.isSubmitting}
            >
              <Save size={18} className="me-1" />
              {formik.isSubmitting ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate(`/pages-detail/${id}`)}
            >
              Cancel
            </button>
          </div>

          <Row>
            {/* Left Column - Editable Fields */}
            <Col lg={8}>
              <Card>
                <CardBody>
                  {/* Title Field - EDITABLE */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="title">
                      Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="title"
                      name="title"
                      className="bg-transparent"
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "600",
                      }}
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.title && !!formik.errors.title}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <FormFeedback>{formik.errors.title}</FormFeedback>
                    )}
                  </FormGroup>
                  
                  {/* Subtitle Field - EDITABLE */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="subtitle">Sub Title</Label>
                    <Input
                      id="subtitle"
                      name="subtitle"
                      type="textarea"
                      rows={2}
                      className="bg-transparent"
                      style={{
                        fontSize: "1.1rem",
                      }}
                      value={formik.values.subtitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.subtitle && !!formik.errors.subtitle}
                    />
                    {formik.touched.subtitle && formik.errors.subtitle && (
                      <FormFeedback>{formik.errors.subtitle}</FormFeedback>
                    )}
                  </FormGroup>
                  
                  {/* Content Field - EDITABLE */}
                  <FormGroup className="mb-3">
                    <Label>
                      Content <span className="text-danger">*</span>
                    </Label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={formik.values.content}
                      onChange={(event, editor) =>
                        formik.setFieldValue("content", editor.getData())
                      }
                      onBlur={() => formik.setFieldTouched("content", true)}
                    />
                    {formik.touched.content && formik.errors.content && (
                      <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                        {formik.errors.content}
                      </div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Right Column - Read-Only Fields */}
            <Col lg={4}>
              {/* Featured Media - READ ONLY */}
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label className="mb-0">Featured Media</Label>
                    <Badge color="secondary" pill>Read Only</Badge>
                  </div>

                  {featuredMedia ? (
                    <div className="mt-2 text-center">
                      {featuredMedia.mimeType?.startsWith("image") && (
                        <img
                          src={`${imageBaseApi}${featuredMedia?.url}`}
                          alt={featuredMedia.fileName}
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            borderRadius: "6px",
                          }}
                        />
                      )}
                      {featuredMedia.mimeType === "application/pdf" && (
                        <a
                          href={featuredMedia.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block mt-2"
                        >
                          📄 {featuredMedia.fileName}
                        </a>
                      )}
                      {!featuredMedia.mimeType?.startsWith("image") &&
                        featuredMedia.mimeType !== "application/pdf" && (
                          <span className="d-block mt-2">
                            ❓ {featuredMedia.fileName}
                          </span>
                        )}
                    </div>
                  ) : (
                    <p className="text-muted mt-2">No media selected</p>
                  )}
                  
                  <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                    <i className="ri-information-line"></i> To change featured media, use the main form page.
                  </div>
                </CardBody>
              </Card>

              {/* Meta Data - READ ONLY */}
              <Card className="mt-3">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <span>Meta Data</span>
                  <Badge color="secondary" pill>Read Only</Badge>
                </CardHeader>
                <CardBody>
                  {/* Meta Title - READ ONLY */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="metaTitle">
                      Meta Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="metaTitle"
                      name="metaTitle"
                      value={pageData?.metaTitle || ""}
                      disabled
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                  </FormGroup>
                  
                  {/* Meta Description - READ ONLY */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="metaDescription">
                      Meta Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="metaDescription"
                      name="metaDescription"
                      type="textarea"
                      rows={3}
                      value={pageData?.metaDescription || ""}
                      disabled
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                  </FormGroup>
                  
                  {/* Status - READ ONLY */}
                  <div className="mb-0">
                    <Label>Status</Label>
                    <div>
                      <Badge 
                        color={pageData?.status === "draft" ? "warning" : "success"}
                        className="px-3 py-2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {pageData?.status || "draft"}
                      </Badge>
                    </div>
                  </div>

                  <div className="text-muted mt-3" style={{ fontSize: "0.85rem" }}>
                    <i className="ri-information-line"></i> To change meta data and status, use the main form page.
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
};

export default PageEditor;